/**
 * La tabla de la pestana Stock de Inventario (INV-01): cada producto con su
 * foto, su stock y los tres movimientos que se anotan desde la fila.
 *
 * Los tres botones abren el mismo formulario, ya en el tipo que se toco: la
 * pregunta "que paso" se contesta con el boton, no despues en un desplegable.
 * La foto esta por lo mismo que en Productos: un producto se reconoce antes
 * por la foto que por el nombre.
 */
import { Link } from 'react-router-dom';

import type { ProductoAdminResumen, TipoMovimientoManual } from '@bandu/compartido';

import { fecha, numero } from '../../utilidades/formato';
import { nombreConVariante } from '../../utilidades/producto';
import { rutas } from '../../utilidades/rutas';
import { Imagen } from '../comunes/Imagen';
import { Tabla } from './Tabla';

export interface TablaStockProps {
  productos: ProductoAdminResumen[];
  umbral: number;
  /** La URL de hoy, para que "Volver" desde el detalle regrese aca con los filtros. */
  volver: string;
  onAnotar(producto: ProductoAdminResumen, tipo: TipoMovimientoManual): void;
}

function EtiquetaStock({ stock, umbral }: { stock: number; umbral: number }) {
  if (stock <= 0) return <span className="etq etq-critico">Agotado</span>;
  return (
    <span className={stock <= umbral ? 'etq etq-alerta num' : 'etq etq-neutra num'}>
      {numero(stock)}
    </span>
  );
}

const ACCIONES: { tipo: TipoMovimientoManual; texto: string; accion: string }[] = [
  { tipo: 'ingreso', texto: '+ Llegó', accion: 'Anotar mercadería que llegó de' },
  { tipo: 'venta_externa', texto: '− Vendí', accion: 'Anotar una venta por fuera de' },
  { tipo: 'ajuste', texto: 'Contar', accion: 'Contar el stock de' },
];

export function TablaStock({ productos, umbral, volver, onAnotar }: TablaStockProps) {
  return (
    <Tabla
      titulo="Stock por producto"
      encabezados={
        <>
          <th scope="col">Producto</th>
          <th scope="col">Categoria</th>
          <th scope="col" className="der">
            Stock
          </th>
          <th scope="col">Ultimo movimiento</th>
          <th scope="col" className="der">
            <span className="solo-lectores">Anotar</span>
          </th>
        </>
      }
    >
      {productos.map((producto) => {
        const nombre = nombreConVariante(producto);
        return (
          <tr key={producto.id}>
            <th scope="row">
              <Link
                className="celda-prod"
                to={rutas.administracion.productoDetalle(producto.id)}
                state={{ volver }}
              >
                {producto.imagen !== null ? (
                  <Imagen
                    className="mini-img"
                    src={producto.imagen.url}
                    alt=""
                    ancho={producto.imagen.ancho}
                    alto={producto.imagen.alto}
                  />
                ) : (
                  <span className="mini-img sin-foto" aria-hidden="true" />
                )}
                <span>
                  <span className="n">{nombre}</span>
                  <span className="s">{producto.sku}</span>
                </span>
              </Link>
            </th>
            <td>{producto.categoriaNombre}</td>
            <td className="der">
              <EtiquetaStock stock={producto.stock} umbral={umbral} />
            </td>
            <td className="sin-cortes">
              {producto.ultimoMovimientoEn !== null ? (
                <time dateTime={producto.ultimoMovimientoEn}>
                  {fecha(producto.ultimoMovimientoEn)}
                </time>
              ) : (
                <span className="texto-suave">Nunca</span>
              )}
            </td>
            <td className="der">
              <span className="acciones-fila acciones-stock">
                {ACCIONES.map((accion) => (
                  <button
                    key={accion.tipo}
                    type="button"
                    className="btn btn-xs btn-contorno"
                    aria-label={`${accion.accion} ${nombre}`}
                    onClick={() => {
                      onAnotar(producto, accion.tipo);
                    }}
                  >
                    {accion.texto}
                  </button>
                ))}
              </span>
            </td>
          </tr>
        );
      })}
    </Tabla>
  );
}
